import { useEffect, useState, useContext, useRef } from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { getNewRelicConstant, triggerNewRelic, triggerTargetingInputsMetric } from '../utils/newRelicHelpers';
import { getBannerQueryParams, getQueryParams } from '../utils/requestUrlHelpers';
import { getOrganicSkus } from '../utils/queryParamsHelpers';
import { getFromBannerApi } from '../utils/axios';
import { determineAdServer } from '../utils/utils';
import { bannerTypes, bannerImplementationTypes } from '../constants/constants';

const shouldSkipSkyscraperFetch = (requestConfig) => (
  requestConfig?.bannerType === bannerTypes.SKYSCRAPER
  && (requestConfig.experienceContext.channel !== 'desktop' || requestConfig.schema === 'event_browse_sponsored')
);

const isDataResolved = (requestConfig, experienceContext) => {
  if (requestConfig.schema === 'browse_sponsored' || requestConfig.schema === 'search_sponsored') {
    return !!(experienceContext?.mounted && requestConfig?.browserId && requestConfig?.pageContext?.data);
  }
  return !!(experienceContext?.mounted && requestConfig?.browserId);
};

/**
 *Make api request to sponsored banner api
 * */
const useFetch = (requestConfig) => {

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [apiRequestTimestamp, setApiRequestTimestamp] = useState(null);
  const [apiResponseTimestamp, setApiResponseTimestamp] = useState(null);
  const [adServer, setAdServer] = useState(null);
  const silentDegradationMetricRef = useRef(false);
  const experienceContext = useContext(ExperienceContext);
  const hosts = experienceContext.hosts;
  const bannerHost = hosts.apionline;

  const fetchData = async () => {
    let response = {};
    try {
      let queryParams;
      if (requestConfig.schema === 'cat_sponsored' || requestConfig.schema === 'browse_sponsored' || requestConfig.schema === 'search_sponsored') {
        queryParams = getQueryParams(requestConfig);
      } else if (requestConfig.schema === 'hp_sponsored' || requestConfig.schema === 'hp_sponsored_auth' || requestConfig.schema === 'localad_sponsored') {
        queryParams = getBannerQueryParams(requestConfig);
      }
      if (requestConfig.schema === 'browse_sponsored' && !queryParams.category) {
        triggerNewRelic(queryParams.adType, `BANNER-API-BLOCKED`);
        return null;
      }
      return await getFromBannerApi(bannerHost, { params: queryParams, withCredentials: true });
    } catch (err) {

      if (err.name === 'AbortError') {
        console.log('Fetch aborted');
      } else {
        setError(err.message);
        setStatus(response.status);
      }
      return err;
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    if (shouldSkipSkyscraperFetch(requestConfig)) return undefined;
    if (isDataResolved(requestConfig, experienceContext)) {
      const bannerType = getNewRelicConstant(requestConfig.bannerType);
      if (!silentDegradationMetricRef.current && !requestConfig?.isBannerSuppressed) {
        triggerTargetingInputsMetric({
          schema: requestConfig.schema,
          bannerType: requestConfig.bannerType,
          hasMatchProducts: !!getOrganicSkus(requestConfig),
          hasCustomerIdentifier: !!requestConfig.guestUserID,
          implementation: bannerImplementationTypes.LEGACY,
        });
        silentDegradationMetricRef.current = true;
      }
      if (requestConfig.browserId === '123456') {
        triggerNewRelic(bannerType, 'BANNER-API-CALLED-MCVISID-123456');
      }
      if (!requestConfig?.isBannerSuppressed) {
        setApiRequestTimestamp(new Date().getTime());
        fetchData().then((response) => {
          setApiResponseTimestamp(new Date().getTime());
          setData(response?.data);
          setAdServer(determineAdServer(response?.data));
          setStatus(response?.status);
        });
      }
    }
    return () => abortController.abort();
  }, [requestConfig?.browserId, experienceContext?.mounted]);

  return {
    data, status, error, apiResponseTimestamp, apiRequestTimestamp, adServer
  };
};

export default useFetch;
